/**
 * Settings → Typography (native FontPickerView): the UI font, the three
 * editor regions, then sizing sliders and a reset. Each font row pushes its
 * own picker page (`font:<region>`). Picks land live behind the dialog.
 */
import { fontCatalog, fontsById } from "@caliu/styles/fonts";
import type { EditorTypography, FontRegion } from "@caliu/types";
import { EDITOR_TYPOGRAPHY_RANGES } from "@caliu/utils";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { AnalyticsEvent, track } from "../../lib/analytics";
import { canUseFont } from "../../lib/pro";
import { useIsPro } from "../../store/subscription";
import { useUIStore } from "../../store/ui";
import { ProTag } from "../ui/ProTag";
import { cn } from "@/lib/utils";
import { SettingsRow, SettingsSection, settingsSurface, useSettingsCompact } from "./parts";

export const FONT_REGION_TITLE: Record<FontRegion, string> = {
  chrome: "fonts.uiFont",
  prose: "fonts.body",
  headings: "fonts.headings",
  code: "fonts.code",
};

const UI_REGIONS: FontRegion[] = ["chrome"];
const EDITOR_REGIONS: FontRegion[] = ["prose", "headings", "code"];

function fontFamilyStack(id: string): string {
  const entry = fontsById[id];
  if (!entry) return "sans-serif";
  const fallback =
    entry.category === "serif"
      ? 'Georgia, "Times New Roman", serif'
      : entry.category === "mono"
        ? '"Fira Code", "SF Mono", monospace'
        : "system-ui, sans-serif";
  if (entry.fontsourceVariant === "variable") {
    return `"${entry.family} Variable", "${entry.family}", ${fallback}`;
  }
  return `"${entry.family}", ${fallback}`;
}

export function TypographySettings() {
  const { t } = useTranslation();
  const regions = useUIStore((s) => s.fontPreferences.regions);
  const openSettings = useUIStore((s) => s.openSettings);
  const typography = useUIStore((s) => s.editorTypography);
  const setTypography = useUIStore((s) => s.setEditorTypography);
  const resetFontPreferences = useUIStore((s) => s.resetFontPreferences);
  const resetEditorTypography = useUIStore((s) => s.resetEditorTypography);
  const compact = useSettingsCompact();
  // A row of its own: a card on desktop, a slot in the phone's group.
  const sliderRow = cn(compact ? "px-4 py-3" : cn("px-3.5 py-2.5", settingsSurface(false)));

  const fontRow = (region: FontRegion) => (
    <SettingsRow
      key={region}
      label={t(FONT_REGION_TITLE[region])}
      value={
        <span className="text-[15px] text-ink" style={{ fontFamily: fontFamilyStack(regions[region]) }}>
          {fontsById[regions[region]]?.family}
        </span>
      }
      onClick={() => openSettings(`font:${region}`)}
    />
  );

  return (
    <>
      <SettingsSection title={t("fonts.ui")}>
        {UI_REGIONS.map(fontRow)}
      </SettingsSection>
      <SettingsSection title={t("fonts.editor")}>
        {EDITOR_REGIONS.map(fontRow)}
      </SettingsSection>
      <SettingsSection title={t("settings.sizing")}>
        <div className={sliderRow}>
          <ValueSlider
            label={t("editorTypography.fontSize")}
            setting="size"
            value={typography.size}
            format={(v) => `${v} px`}
            onChange={setTypography}
          />
        </div>
        <div className={sliderRow}>
          <ValueSlider
            label={t("editorTypography.lineHeight")}
            setting="lineHeight"
            value={typography.lineHeight}
            format={(v) => v.toFixed(1)}
            onChange={setTypography}
          />
        </div>
        <div className={sliderRow}>
          <ValueSlider
            label={t("editorTypography.paragraphSpacing")}
            setting="paragraphSpacing"
            value={typography.paragraphSpacing}
            format={(v) => `${v} px`}
            onChange={setTypography}
          />
        </div>
      </SettingsSection>
      <Button
        variant="ghost"
        className="self-center border-0 text-[14px] text-ink-secondary"
        onClick={() => {
          resetFontPreferences();
          resetEditorTypography();
        }}
      >
        {t("fonts.reset")}
      </Button>
    </>
  );
}

/** A stepped slider over one typography range, its value read out beside the label. */
function ValueSlider({
  label,
  setting,
  value,
  format,
  onChange,
}: {
  label: string;
  setting: keyof EditorTypography;
  value: number;
  format: (value: number) => string;
  onChange: (setting: keyof EditorTypography, value: number) => void;
}) {
  const { min, max, step } = EDITOR_TYPOGRAPHY_RANGES[setting];
  const compact = useSettingsCompact();
  return (
    <label className="flex flex-col gap-1.5 font-chrome">
      <span className="flex items-baseline justify-between">
        <span
          className={cn(
            // Desktop: a row title like every other pane's; phones keep the
            // small caps of native iOS.
            compact ? "text-[11px] font-semibold uppercase tracking-[0.11em] text-muted" : "text-[14px] text-ink",
          )}
        >
          {label}
        </span>
        <span className="text-[13px] tabular-nums text-ink">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(setting, Number(e.target.value))}
        aria-valuetext={format(value)}
        className="w-full cursor-pointer accent-accent"
      />
      <span className="flex justify-between text-[12px] text-muted">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </span>
    </label>
  );
}

/** Settings → Typography → a region: every catalog font, set in itself. */
export function FontRegionSettings({ region }: { region: FontRegion }) {
  const { t } = useTranslation();
  const isPro = useIsPro();
  const current = useUIStore((s) => s.fontPreferences.regions[region]);
  const setFontForRegion = useUIStore((s) => s.setFontForRegion);
  const openPaywall = useUIStore((s) => s.openPaywall);
  return (
    <SettingsSection>
      {fontCatalog.map((entry) => {
        const locked = !canUseFont(isPro, entry.id);
        const stack = { fontFamily: fontFamilyStack(entry.id) };
        return (
          <SettingsRow
            key={entry.id}
            label={<span style={stack}>{entry.family}</span>}
            subtitle={<span style={stack}>{t("common.sampleSentence")}</span>}
            accessory="none"
            trailing={
              locked ? (
                <ProTag className="shrink-0" />
              ) : entry.id === current ? (
                <Icon name="check" size={15} className="shrink-0 text-accent" />
              ) : undefined
            }
            onClick={() => {
              if (locked) return openPaywall("font");
              track(AnalyticsEvent.FontChanged, { font_id: entry.id });
              setFontForRegion(region, entry.id);
            }}
          />
        );
      })}
    </SettingsSection>
  );
}
